import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSkillResult } from '../../../services/assessmentService';

const labels = ['Technical', 'Analytical', 'Communication', 'Leadership', 'Ethics'];

function buildRadarPoints(scores) {
  const center = 120;
  const radius = 90;
  return scores.map((score, idx) => {
    const angle = (Math.PI * 2 * idx) / scores.length - Math.PI / 2;
    const r = (Math.max(0, Math.min(100, score)) / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });
}

function polygonString(points) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

export default function ResultPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const preload = async () => {
      const cached = sessionStorage.getItem('skill_result_payload');
      if (cached) {
        setResult(JSON.parse(cached));
        return;
      }

      const sessionId = sessionStorage.getItem('skill_session_id');
      if (sessionId) {
        const payload = await getSkillResult(sessionId);
        setResult(payload);
      }
    };

    preload();
  }, []);

  const sectionRows = useMemo(() => result?.sectionScores || [], [result]);

  const scoreByCode = useMemo(() => {
    const map = {
      SKL_TK: 0,
      SKL_AT: 0,
      SKL_CS: 0,
      SKL_TL: 0,
      SKL_PE: 0
    };

    sectionRows.forEach((row) => {
      map[row.sectionCode] = row.score;
    });

    return map;
  }, [sectionRows]);

  const radarScores = [
    scoreByCode.SKL_TK,
    scoreByCode.SKL_AT,
    scoreByCode.SKL_CS,
    scoreByCode.SKL_TL,
    scoreByCode.SKL_PE
  ];

  const radarPoints = buildRadarPoints(radarScores);

  const employabilityScore = Math.round(Number(result?.totalScore || 0));
  const readiness = employabilityScore >= 80 ? 'Industry Ready' : employabilityScore >= 60 ? 'Developing' : 'Foundation Level';

  const recommendations = result?.recommendations || [
    'Practice scenario-based decision making with timed sets.',
    'Improve business writing clarity with concise drafts and revisions.',
    'Review structured problem-solving frameworks for workplace cases.'
  ];

  if (!result) {
    return <div className="min-h-screen skill-intro-bg flex items-center justify-center text-white">Loading results...</div>;
  }

  return (
    <div className="min-h-screen skill-intro-bg px-4 py-10 text-white">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-jakarta text-center">ProEdge Skill Assessment</h1>
        <p className="text-center text-white/70 mt-2">Professional Skills Performance Summary</p>

        <div className="mt-8 skill-panel rounded-3xl p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-white/70">Employability Score</p>
              <div className="mt-3 relative h-40 w-40">
                <svg viewBox="0 0 120 120" className="h-40 w-40">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="10" />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 50}
                    strokeDashoffset={(1 - employabilityScore / 100) * 2 * Math.PI * 50}
                    transform="rotate(-90 60 60)"
                  />
                  <text x="60" y="67" textAnchor="middle" className="fill-white text-xl font-bold">{employabilityScore}</text>
                </svg>
              </div>
              <span className="inline-block mt-2 rounded-full border border-emerald-300/30 bg-emerald-500/20 px-3 py-1 text-sm text-emerald-200">
                {readiness}
              </span>
            </div>

            <div className="flex justify-center">
              <svg viewBox="0 0 240 240" className="w-[260px] h-[260px]">
                {[1, 2, 3, 4].map((layer) => {
                  const layerPoints = buildRadarPoints(Array(5).fill((layer / 4) * 100));
                  return (
                    <polygon
                      key={`grid_${layer}`}
                      points={polygonString(layerPoints)}
                      fill="none"
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="1"
                    />
                  );
                })}
                <polygon points={polygonString(radarPoints)} fill="rgba(16,185,129,0.35)" stroke="#34d399" strokeWidth="2" />
                {labels.map((label, idx) => {
                  const angle = (Math.PI * 2 * idx) / labels.length - Math.PI / 2;
                  const x = 120 + 105 * Math.cos(angle);
                  const y = 120 + 105 * Math.sin(angle);
                  return (
                    <text key={label} x={x} y={y} textAnchor="middle" className="fill-white text-[11px]">
                      {label}
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/20 text-white/70">
                  <th className="py-2">Section</th>
                  <th className="py-2">Score</th>
                  <th className="py-2">Time Taken</th>
                  <th className="py-2">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {sectionRows.map((row) => (
                  <tr key={row.sectionCode} className="border-b border-white/10">
                    <td className="py-2">{row.sectionName}</td>
                    <td className="py-2">{row.score}%</td>
                    <td className="py-2">{row.timeTaken}</td>
                    <td className="py-2">{row.accuracy}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 rounded-xl border border-white/20 bg-white/10 p-4">
            <p className="text-sm text-white/75">Top Recommendations</p>
            <ul className="mt-2 text-sm space-y-1">
              {recommendations.slice(0, 3).map((item) => <li key={item}>• {item}</li>)}
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2.5 font-semibold"
            >
              Download Skill Report
            </button>
            <button
              type="button"
              onClick={() => navigate('/assessment')}
              className="rounded-xl border border-white/25 bg-white/10 px-4 py-2.5"
            >
              Back to Hub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
